<script>
/**
 * The appointments this brand's bot took.
 *
 * Two views of one list, because the desk asks two different questions. The
 * table answers "what is outstanding" and carries the actions. The calendar
 * answers "what does Tuesday look like".
 *
 * Dragging a card there does not move the appointment on its own. The shared
 * calendar lets cards be dragged in the week and day views, and a move has to
 * be accepted by the calendar on the other side before it is true here — so a
 * drag opens the change form on the slot it was dropped on, and the card snaps
 * back until that is confirmed. A card that stayed where it was dropped while
 * the real booking never moved would be the worst of the three outcomes.
 *
 * ── What the desk can change ────────────────────────────────────────────
 *   confirm / complete   our row only — no calendar side, cannot half-fail
 *   where it happens     same
 *   when it happens      goes through the bot, which moves the Cal.com slot
 *                        first and refuses the whole thing if that refuses
 *   cancel               same, in reverse
 *
 * An appointment the customer described too vaguely to place ("sometime next
 * week") has no resolved time. It stays in the table and off the calendar,
 * flagged, which is honest: inventing a Tuesday would put a fiction in front
 * of staff who would then turn up for it.
 */
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import CustomFullCalendar from '@/components/CustomFullCalendar'
import i18n from '@/i18n'
import { sharedHelper, tenantHelper } from '@/utils'
import { appointmentService } from '@/helpers/services/appointment.service'

const STATUS_COLOUR = {
  requested: '#f0ad4e',
  confirmed: '#1f9d55',
  completed: '#6c757d',
  cancelled: '#c0392b',
}

export default {
  components: { Layout, PageHeader, CustomFullCalendar },
  data() {
    return {
      rows: [],
      loading: true,
      view: 'list',
      status: '',
      saving: false,
      /** The one being changed, and the form beside it. */
      edit: null,
      form: { date: '', time: '', location: '', reason: '' },
    }
  },
  computed: {
    items() {
      // Top-level, not under Admin — the desk reaches this from the sidebar.
      return [{ text: i18n.t('module.appointment'), active: true }]
    },
    /** No brand selected: the list spans brands, so each row has to say which. */
    allBrands() {
      return tenantHelper.current() === null
    },
    statusOptions() {
      return ['', 'requested', 'confirmed', 'completed', 'cancelled']
    },
    /** Only the ones with a real moment can be drawn. */
    events() {
      return this.rows
        .filter((r) => r.scheduledAt)
        .map((r) => ({
          id: r.apptRef,
          title: this.allBrands
            ? `${r.brandName || r.tenantId} · ${r.name}`
            : `${r.name} · ${r.purpose || ''}`.trim(),
          start: r.scheduledAt,
          backgroundColor: STATUS_COLOUR[r.status] || '#6c757d',
          borderColor: STATUS_COLOUR[r.status] || '#6c757d',
        }))
    },
    undated() {
      return this.rows.filter((r) => !r.scheduledAt).length
    },
    indicator() {
      return Object.keys(STATUS_COLOUR).map((k) => ({ name: this.statusLabel(k), color: STATUS_COLOUR[k] }))
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    /**
     * The shared calendar mounts its events from a watcher on the data prop,
     * which has already fired by the time this view is switched on — so with
     * rows in hand the frame draws empty. Ask it to mount them once it is
     * actually in the DOM.
     */
    async showCalendar() {
      this.view = 'calendar'
      await this.$nextTick()
      this.drawCalendar()
    },
    drawCalendar() {
      const cal = this.$refs.cal
      if (cal && typeof cal.refreshCalendarEvents === 'function') cal.refreshCalendarEvents()
    },
    statusLabel(s) {
      const key = 'appointment.status_' + s
      return this.$te(key) ? this.$t(key) : s
    },
    /** What the customer said, and what it resolved to — the promise and the slot. */
    when(r) {
      const said = [r.preferredDate, r.preferredTime].filter(Boolean).join(' ')
      if (!r.scheduledAt) return said || '—'
      return new Date(r.scheduledAt).toLocaleString()
    },
    async load() {
      this.loading = true
      try {
        const res = await appointmentService.list(
          this.status ? { status: this.status, limit: 200 } : { limit: 200 },
        )
        this.rows = res.data || []
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.loading = false
      }
      // After `loading`, not inside it: the spinner replaces the calendar in
      // the DOM, and a redraw aimed at a component that is not mounted lands
      // nowhere — which is how a reload used to leave the month blank.
      if (this.view === 'calendar') {
        await this.$nextTick()
        this.drawCalendar()
      }
    },
    /** @param {Date|null} at the slot to propose, defaulting to where it is now */
    openEdit(r, at = null) {
      this.edit = r
      const d = at || (r.scheduledAt ? new Date(r.scheduledAt) : null)
      this.form = {
        date: d ? this.localDate(d) : '',
        time: d ? this.localTime(d) : '',
        location: r.location || '',
        reason: '',
      }
    },
    /** Local wall-clock, not toISOString — that shifts the day near midnight. */
    localDate(d) {
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    localTime(d) {
      const p = (n) => String(n).padStart(2, '0')
      return `${p(d.getHours())}:${p(d.getMinutes())}`
    },
    byRef(ref) {
      return this.rows.find((r) => r.apptRef === ref) || null
    },
    onEventClick(e) {
      const row = this.byRef(e.id)
      if (row && row.status !== 'cancelled') this.openEdit(row)
    },
    /**
     * A card was dragged. The calendar reports the slot in UTC; offer it as the
     * new time and put the card back where it was until the move is accepted.
     */
    async onEventDragged(e) {
      const row = this.byRef(e.id)
      const dropped = e.start ? new Date(`${String(e.start).replace(' ', 'T')}Z`) : null
      await this.load()
      if (row && dropped && !isNaN(dropped.getTime())) this.openEdit(row, dropped)
    },
    /**
     * Take the job.
     *
     * Not a status edit. The server reads who pressed it from the token and
     * puts that person on the Cal.com booking as a guest, so the invite
     * arrives in their own mailbox and accepting it puts the appointment in
     * their own calendar.
     *
     * The calendar can fail while the claim succeeds, and the two are
     * reported separately on purpose. "Taken" with no invite is still true
     * and still useful; telling somebody their calendar has it when it does
     * not is the failure that costs a missed appointment.
     */
    async take(r) {
      this.saving = true
      try {
        const res = await appointmentService.claim(r.apptRef)
        if (res.booking_error) {
          sharedHelper.alertErrorMessageWithoutCode(
            i18n.t('appointment.takenNoInvite', [this.bookingProblem(res.booking_error)]),
          )
        } else {
          sharedHelper.alertSuccessMessage(i18n.t('appointment.taken'))
        }
        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },
    /** The handful the desk can actually act on; anything else shown as-is. */
    bookingProblem(code) {
      const key = 'appointment.booking_' + code
      return this.$te(key) ? this.$t(key) : code
    },
    async complete(r) {
      await this.patch(r, { status: 'completed' })
    },
    async patch(r, payload) {
      this.saving = true
      try {
        await appointmentService.update(r.apptRef, payload)
        sharedHelper.alertSuccessMessage(i18n.t('appointment.saved'))
        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },
    /** Place only — nothing on the calendar changes, so it cannot half-fail. */
    async saveLocation() {
      const row = this.edit
      this.edit = null
      await this.patch(row, { location: this.form.location })
    },
    /**
     * The offset is not decoration. The bot reads the wall-clock out of this
     * string for what the customer is told, and hands the same string to
     * Cal.com as an instant; without an offset those two agree only when the
     * server happens to sit in the same timezone as whoever is typing.
     */
    startIso() {
      const mins = -new Date(`${this.form.date}T${this.form.time}:00`).getTimezoneOffset()
      const sign = mins < 0 ? '-' : '+'
      const abs = Math.abs(mins)
      const p = (n) => String(n).padStart(2, '0')
      return `${this.form.date}T${this.form.time}:00${sign}${p(Math.floor(abs / 60))}:${p(abs % 60)}`
    },
    /** Time: the calendar has to accept it before it is true here. */
    async saveTime() {
      if (!this.form.date || !this.form.time) return
      this.saving = true
      try {
        await appointmentService.reschedule(this.edit.apptRef, {
          new_start_iso: this.startIso(),
          ...(this.form.reason ? { reason: this.form.reason } : {}),
          ...(this.form.location !== (this.edit.location || '') ? { location: this.form.location } : {}),
        })
        sharedHelper.alertSuccessMessage(i18n.t('appointment.moved'))
        this.edit = null
        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },
    async cancel(r) {
      // Naming both buttons matters here more than usual: the default pair is
      // "Delete" and "Cancel", and "Cancel" as the way to NOT cancel an
      // appointment is the one misread that cannot be undone.
      const ok = await sharedHelper.confirmDestructive({
        title: i18n.t('appointment.cancelTitle'),
        text: i18n.t('appointment.cancelText', [r.name, this.when(r)]),
        confirmText: i18n.t('appointment.cancelYes'),
        cancelText: i18n.t('appointment.cancelNo'),
      })
      if (!ok) return
      this.saving = true
      try {
        await appointmentService.cancel(r.apptRef, 'cancelled from the console')
        sharedHelper.alertSuccessMessage(i18n.t('appointment.cancelled'))
        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },
    readError(e) {
      const d = e && e.response && e.response.data
      return (d && (d.error || d.message)) || i18n.t('message.error')
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('module.appointment')" :items="items" />

    <div class="card">
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-center mb-3">
            <div class="btn-group btn-group-sm mr-3">
              <button type="button" class="btn" :class="view === 'list' ? 'btn-primary' : 'btn-outline-secondary'" @click="view = 'list'">{{ $t('appointment.viewList') }}</button>
              <button type="button" class="btn" :class="view === 'calendar' ? 'btn-primary' : 'btn-outline-secondary'" @click="showCalendar">{{ $t('appointment.viewCalendar') }}</button>
            </div>
            <b-form-select v-model="status" size="sm" class="status-pick mr-3" @change="load">
              <b-form-select-option v-for="s in statusOptions" :key="s" :value="s">{{ s ? statusLabel(s) : $t('appointment.allStatuses') }}</b-form-select-option>
            </b-form-select>
            <span class="small text-muted">{{ $t('appointment.count', [rows.length]) }}</span>
          </div>

          <div v-if="loading" class="small text-muted">{{ $t('label.loading') }}</div>

          <!-- ── Table ──────────────────────────────────────────────────── -->
          <div v-else-if="view === 'list'">
            <div v-if="!rows.length" class="small text-muted">{{ $t('appointment.empty') }}</div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                  <tr>
                    <th v-if="allBrands">{{ $t('appointment.brand') }}</th>
                    <th>{{ $t('appointment.ref') }}</th>
                    <th>{{ $t('appointment.who') }}</th>
                    <th>{{ $t('appointment.what') }}</th>
                    <th>{{ $t('appointment.when') }}</th>
                    <th>{{ $t('appointment.where') }}</th>
                    <th>{{ $t('appointment.status') }}</th>
                    <th>{{ $t('appointment.claimedBy') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in rows" :key="r.apptRef">
                    <td v-if="allBrands" class="small">{{ r.brandName || r.tenantId }}</td>
                    <td><code class="small">{{ r.apptRef }}</code></td>
                    <td>{{ r.name }}<div class="small text-muted">{{ r.contactPhone }}</div></td>
                    <td class="small">{{ r.purpose }}</td>
                    <td class="small">
                      {{ when(r) }}
                      <!-- Said but never pinned down: it is not on the calendar,
                           and the desk should be told rather than discover it. -->
                      <b-badge v-if="!r.scheduledAt" variant="warning" class="ml-1">{{ $t('appointment.noTime') }}</b-badge>
                    </td>
                    <td class="small">{{ r.location || '—' }}</td>
                    <td><span class="st-chip" :style="{ color: '#333' }">{{ statusLabel(r.status) }}</span></td>
                    <td class="small">
                      <template v-if="r.claimedByName">
                        {{ r.claimedByName }}
                        <div class="text-muted">{{ r.claimedByEmail }}</div>
                      </template>
                      <span v-else class="text-muted">{{ $t('appointment.unclaimed') }}</span>
                    </td>
                    <td class="text-right text-nowrap">
                      <b-button v-if="!r.claimedByEmail && r.status !== 'cancelled' && r.status !== 'completed'" size="sm" variant="outline-success" class="mr-1" :disabled="saving" @click="take(r)">{{ $t('appointment.take') }}</b-button>
                      <b-button v-if="r.status === 'confirmed'" size="sm" variant="outline-secondary" class="mr-1" :disabled="saving" @click="complete(r)">{{ $t('appointment.complete') }}</b-button>
                      <b-button v-if="r.status !== 'cancelled'" size="sm" variant="outline-primary" class="mr-1" @click="openEdit(r)">{{ $t('appointment.change') }}</b-button>
                      <b-button v-if="r.status !== 'cancelled'" size="sm" variant="link" class="text-danger p-0" :disabled="saving" @click="cancel(r)">{{ $t('appointment.cancel') }}</b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── Calendar ───────────────────────────────────────────────── -->
          <div v-else>
            <p v-if="undated" class="small text-muted">{{ $t('appointment.undatedNote', [undated]) }}</p>
            <CustomFullCalendar
              ref="cal"
              :events-data="events"
              initial-view="dayGridMonth"
              calendar-modes="standard"
              :indicator="indicator"
              @leftClick="onEventClick"
              @update="onEventDragged"
            />
          </div>
        </div>
    </div>

    <!-- ── Change one ───────────────────────────────────────────────────── -->
    <b-modal :visible="!!edit" :title="$t('appointment.changeTitle')" hide-footer centered @hidden="edit = null">
      <template v-if="edit">
        <p class="small text-muted">{{ edit.apptRef }} · {{ edit.name }}</p>

        <b-form-group :label="$t('appointment.where')">
          <b-form-input v-model="form.location" :placeholder="$t('appointment.wherePlaceholder')" />
        </b-form-group>
        <div class="text-right mb-3">
          <b-button size="sm" variant="outline-primary" :disabled="saving" @click="saveLocation">{{ $t('appointment.saveLocation') }}</b-button>
        </div>

        <hr />
        <p class="small text-muted">{{ $t('appointment.moveHint') }}</p>
        <div class="d-flex">
          <b-form-group :label="$t('appointment.date')" class="mr-2 flex-grow-1">
            <b-form-input v-model="form.date" type="date" />
          </b-form-group>
          <b-form-group :label="$t('appointment.time')" class="flex-grow-1">
            <b-form-input v-model="form.time" type="time" />
          </b-form-group>
        </div>
        <b-form-group :label="$t('appointment.reason')">
          <b-form-input v-model="form.reason" :placeholder="$t('appointment.reasonPlaceholder')" />
        </b-form-group>
        <div class="text-right">
          <b-button variant="link" size="sm" @click="edit = null">{{ $t('label.cancel') }}</b-button>
          <b-button variant="primary" size="sm" :disabled="saving || !form.date || !form.time" @click="saveTime">
            {{ saving ? $t('label.loading') : $t('appointment.move') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </Layout>
</template>

<style scoped>
.status-pick { max-width: 190px; }
.st-chip {
  display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: .04em;
  padding: 2px 8px; border-radius: 999px; background: #eef0f4;
}
</style>
